import { useEffect } from 'react'
import useCustomer from '@framework/customer/use-customer'
import commerce from '@lib/api/commerce'
import { Layout, SEO } from '@components/common'
import { StaticPage } from '@components/staticpage'
import { Container, Text } from '@components/ui'
import { getGlobalContentContext } from '@drf/getGlobalContentContext'
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import type { TermsConditionsPage as TermsConditionsPageType } from '@generated/model/models'
import { getPageContext } from '@drf/getPageContext'
import { useUI } from '@components/ui/context'
import { useGlobalContentContext } from '@context/GlobalContentContext'

export async function getStaticProps({
  preview,
  locale,
  locales,
}: GetStaticPropsContext) {
  const config = { locale, locales }
  const productsPromise = commerce.getAllProducts({
    variables: { first: 50 },
    config,
    preview,
    // Saleor provider only
    ...({ featured: true } as any),
  })
  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const { products } = await productsPromise
  const { pages } = await pagesPromise

  const global = await getGlobalContentContext()
  const page: TermsConditionsPageType = await getPageContext(
    'termsconditionspage'
  )

  return {
    props: {
      products,
      pages,
      global,
      page,
    },
    revalidate: 60,
  }
}

export default function Privacy({
  products,
  global,
  page,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { setData } = useGlobalContentContext()

  useEffect(() => {
    setData(global)
  }, [global, setData])

  const { setPageTheme, setContainerFullWidth } = useUI()

  useEffect(() => {
    setContainerFullWidth()
    setPageTheme('DARK')
  }, [])

  return (
    <>
      <StaticPage data={page} id="privacy-page" />
      <SEO
        title={page.meta_title}
        description={page.meta_description}
        openGraph={{
          type: 'website',
          title: page.meta_title,
          description: page.meta_description,
          images: [
            {
              url: page.meta_image as string,
              width: '800',
              height: '600',
              alt: page.meta_title,
            },
          ],
        }}
      />
    </>
  )
}

Privacy.Layout = Layout
